<template>
    <div class="nch--wiki-discussion">
        <div v-if="!canPost" class="alert alert-secondary py-2 small mb-3 nch--discussion-closed"><i class="fas fa-lock mr-1"></i>{{ $t('wiki.discussion.closed') }}</div>
        <div v-else class="card bg-light border-0 mb-3">
            <div class="card-body py-3">
                <div v-if="replyTo" class="small mb-2">
                    {{ $t('wiki.discussion.replying_to', { user: replyTo.user || '—' }) }}
                    <a href="#" class="ml-2" @click.prevent="replyTo = null">×</a>
                </div>
                <div v-else class="d-flex flex-wrap mb-2">
                    <button v-for="t in types" :key="t" type="button" class="btn btn-sm mr-1 mb-1" :class="type === t ? 'text-white' : 'btn-light'" :style="type === t ? { backgroundColor: typeColor(t) } : {}" @click="type = t">
                        <i :class="typeIcon(t)" class="mr-1"></i>{{ $t(`wiki.discussion.types.${t}`) }}
                    </button>
                </div>
                <textarea v-model.trim="body" class="form-control nch--comment-input" rows="3" maxlength="5000" :placeholder="$t('wiki.discussion.write')"></textarea>
                <div class="d-flex justify-content-end mt-2">
                    <button type="button" class="btn btn-sm btn-primary nch--comment-publish" :style="{ backgroundColor: color.primary, borderColor: color.primary }" :disabled="!body || saving" @click="publish">
                        <i class="far fa-paper-plane mr-1"></i>{{ $t('wiki.discussion.publish') }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="loading" class="text-center text-muted py-4"><i class="fas fa-circle-notch fa-spin"></i></div>
        <div v-else-if="!comments.length" class="text-muted small">{{ $t('wiki.discussion.empty') }}</div>
        <div v-for="c in comments" v-else :key="c.id" class="nch--comment mb-3">
            <div class="d-flex align-items-start">
                <span class="nch--comment-type mr-2" :style="{ backgroundColor: typeColor(c.type) }" :title="$t(`wiki.discussion.types.${c.type}`)"><i :class="typeIcon(c.type)"></i></span>
                <div class="flex-grow-1 min-w-0">
                    <div class="small">
                        <strong>{{ c.user || '—' }}</strong>
                        <span v-if="c.is_teacher" class="badge badge-pill badge-primary ml-1 nch--teacher" :style="{ backgroundColor: color.primary }">{{ $t('wiki.discussion.teacher') }}</span>
                        <span class="badge badge-pill ml-1 text-white" :style="{ backgroundColor: typeColor(c.type) }">{{ $t(`wiki.discussion.types.${c.type}`) }}</span>
                        <span class="text-muted">· {{ date(c.created_at) }}</span>
                    </div>
                    <div class="nch--comment-body">{{ c.body }}</div>
                    <a v-if="canPost" href="#" class="small" :style="{ color: color.primary }" @click.prevent="replyTo = c"><i class="fas fa-reply mr-1"></i>{{ $t('wiki.discussion.reply') }}</a>
                    <div v-for="r in c.replies" :key="r.id" class="nch--reply mt-2 pl-3 border-left">
                        <div class="small">
                            <strong>{{ r.user || '—' }}</strong>
                            <span v-if="r.is_teacher" class="badge badge-pill badge-primary ml-1 nch--teacher" :style="{ backgroundColor: color.primary }">{{ $t('wiki.discussion.teacher') }}</span>
                            <span class="text-muted">· {{ date(r.created_at) }}</span>
                        </div>
                        <div class="nch--comment-body">{{ r.body }}</div>
                        <a v-if="canPost" href="#" class="small" :style="{ color: color.primary }" @click.prevent="replyTo = r"><i class="fas fa-reply mr-1"></i>{{ $t('wiki.discussion.reply') }}</a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { friendlyDate } from './wikiHelpers';

/**
 * Discussão da página (backlog#343): histórico completo com comentários por
 * tipo, respostas encadeadas, selo PROFESSOR; publicação bloqueada quando a
 * wiki está encerrada ou fora do prazo.
 */
export default {
    name: 'WikiDiscussion',

    props: {
        wikiId: { type: Number, required: true },
        pageId: { type: Number, required: true },
        canPost: { type: Boolean, default: true },
    },

    emits: ['published'],

    data: () => ({ comments: [], loading: true, saving: false, type: 'question', body: '', replyTo: null, types: ['question', 'suggestion', 'justification', 'debate'] }),

    computed: { color() { return this.$store.getters.COLOR || {}; } },

    watch: { pageId() { this.load(); } },

    created() { this.load(); },

    methods: {
        date(v) { return friendlyDate(v, this.$t.bind(this)); },
        typeColor(t) { return { question: '#1C79BE', suggestion: '#2DCE89', justification: '#F5A623', debate: '#8965E0' }[t] || '#8898aa'; },
        typeIcon(t) { return { question: 'far fa-question-circle', suggestion: 'far fa-lightbulb', justification: 'fas fa-balance-scale', debate: 'far fa-comments' }[t] || 'far fa-comment'; },

        load() {
            this.loading = true;
            window.axios.get(`/api/app/wikis/${this.wikiId}/pages/${this.pageId}/comments`)
                .then(({ data }) => { this.comments = data || []; })
                .catch(() => { this.comments = []; })
                .finally(() => { this.loading = false; });
        },

        async publish() {
            this.saving = true;
            try {
                const { data } = await window.axios.post(`/api/app/wikis/${this.wikiId}/pages/${this.pageId}/comments`, { type: this.type, body: this.body, parent_id: this.replyTo ? this.replyTo.id : null });
                this.comments = data.comments || [];
                this.body = ''; this.replyTo = null;
                this.$notify({ type: 'success', message: this.$t('wiki.comment_published') });
                this.$emit('published');
            } catch (e) {
                const res = (e && e.response) || {};
                this.$notify({ type: 'danger', message: (res.data && (res.data.error || res.data.message)) || this.$t('wiki.error') });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.nch--comment-type { display: inline-flex; width: 30px; height: 30px; border-radius: 50%; color: #fff; align-items: center; justify-content: center; flex: 0 0 30px; font-size: 13px; }
.nch--comment-body { white-space: pre-wrap; }
.min-w-0 { min-width: 0; }
</style>
